// [label, unit, digits] descriptor tuple for a raw metric channel.
export type RawMetricEntry = [label: string, unit: string, digits: number];

interface RawEngineDescriptor {
  label?: string;
  shortLabel?: string;
  channels?: Record<string, unknown>;
}

function titleCaseKey(value: unknown): string {
  return String(value ?? "").replace(/-/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const thermalMetricDescriptors: Record<string, RawMetricEntry> = Object.fromEntries([
  ...Array.from({ length: 16 }, (_, index): [string, RawMetricEntry] => {
    const number = index + 1;
    return [`egt${number}`, [`EGT${number}`, "F", 0]];
  }),
  ...Array.from({ length: 16 }, (_, index): [string, RawMetricEntry] => {
    const number = index + 1;
    return [`cht${number}`, [`CHT${number}`, "F", 0]];
  }),
]);

const RAW_METRIC_DICTIONARY: Record<string, RawMetricEntry> = {
  egt: ["EGT avg", "F", 0],
  cht: ["CHT avg", "F", 0],
  ...thermalMetricDescriptors,
  fuelFlow: ["Fuel Flow", "GPH", 1],
  fuelFlow1: ["Fuel Flow", "GPH", 1],
  fuelFlow2: ["Fuel Flow", "GPH", 1],
  fuelFlowLeft: ["Fuel Flow", "GPH", 1],
  fuelFlowRight: ["Fuel Flow", "GPH", 1],
  fuelUsed: ["Fuel Used", "gal", 1],
  fuelUsed1: ["Fuel Used", "gal", 1],
  fuelUsed2: ["Fuel Used", "gal", 1],
  fuelUsedLeft: ["Fuel Used", "gal", 1],
  fuelUsedRight: ["Fuel Used", "gal", 1],
  fuelPressure: ["Fuel Press", "psi", 1],
  fuelPressure1: ["Fuel Pressure", "psi", 1],
  fuelPressure2: ["Fuel Pressure", "psi", 1],
  fuelLeft: ["Left Fuel Quantity", "gal", 1],
  fuelRight: ["Right Fuel Quantity", "gal", 1],
  coolantTemp: ["Coolant Temp", "F", 0],
  coolantPressure: ["Coolant Press", "psi", 1],
  tit: ["TIT", "F", 0],
  tit1: ["TIT", "F", 0],
  tit2: ["TIT", "F", 0],
  upperDeckPressure: ["Upper Deck", "psi", 1],
  inductionTemp: ["Induction Temp", "F", 0],
  compressorDischargeTemp: ["CDT", "F", 0],
  manifoldTemp: ["Manifold Temp", "F", 0],
  boostPressure: ["Boost Press", "psi", 1],
  throttlePosition: ["Throttle", "%", 0],
  dynonCanBoostPressure: ["Dynon CAN Boost Pressure", "", 1],
  dynonCanEngineTime: ["Dynon CAN Engine Time", "", 1],
  dynonCanBoostPressureError: ["Dynon CAN Boost Pressure Error", "", 1],
  dynonCanOilPressureCompensation: ["Dynon CAN Oil Pressure Compensation", "", 1],
  dynonCanTargetWastegatePosition: ["Dynon CAN Target Wastegate Position", "", 1],
  dynonCanAmbientEngineTemp: ["Dynon CAN Ambient Engine Temp", "", 1],
  dynonCanAmbientEnginePressure: ["Dynon CAN Ambient Engine Pressure", "", 1],
  buss: ["Bus Volts", "V", 1],
  amps: ["Amps", "A", 1],
  alternatorAmps: ["Alternator Current", "A", 1],
  batteryAmps: ["Battery Current", "A", 1],
  amps1: ["Amps", "A", 1],
  amps2: ["Amps", "A", 1],
  ampsLeft: ["Amps", "A", 1],
  ampsRight: ["Amps", "A", 1],
  oat: ["OAT", "F", 0],
  carbTemp: ["Carb Temp", "F", 0],
  oilTemp: ["Oil Temp", "F", 0],
  oilTemp1: ["Oil Temp", "F", 0],
  oilTemp2: ["Oil Temp", "F", 0],
  oilTempLeft: ["Oil Temp", "F", 0],
  oilTempRight: ["Oil Temp", "F", 0],
  oilPressure: ["Oil Pressure", "psi", 0],
  oilPressure1: ["Oil Pressure", "psi", 0],
  oilPressure2: ["Oil Pressure", "psi", 0],
  oilPressureLeft: ["Oil Pressure", "psi", 0],
  oilPressureRight: ["Oil Pressure", "psi", 0],
  rpm: ["RPM", "RPM", 0],
  rpm1: ["RPM", "RPM", 0],
  rpm2: ["RPM", "RPM", 0],
  rpmLeft: ["RPM", "RPM", 0],
  rpmRight: ["RPM", "RPM", 0],
  map: ["MAP", "inHg", 1],
  map1: ["MAP", "inHg", 1],
  map2: ["MAP", "inHg", 1],
  mapLeft: ["MAP", "inHg", 1],
  mapRight: ["MAP", "inHg", 1],
  percentPower: ["Engine Power", "%", 0],
  powerRopHp: ["Power ROP", "hp", 0],
  percentPowerRop: ["Power ROP", "%", 0],
  powerLopHp: ["Power LOP", "hp", 0],
  percentPowerLop: ["Power LOP", "%", 0],
  ias: ["IAS", "kt", 0],
  tas: ["TAS", "kt", 0],
  groundSpeed: ["Ground Speed", "kt", 0],
  altitude: ["Altitude", "ft", 0],
  densityAltitude: ["Density Altitude", "ft", 0],
  pressureAltitude: ["Pressure Altitude", "ft", 0],
  baroPressure: ["Baro Pressure", "inHg", 2],
  verticalSpeed: ["Vertical Speed", "fpm", 0],
  pitch: ["Pitch", "deg", 1],
  roll: ["Roll", "deg", 1],
};

function rawMetricDescriptorLabelBase(descriptor: RawEngineDescriptor | null | undefined): string {
  if (!descriptor) return "";
  return String(descriptor.label ?? descriptor.shortLabel ?? "").replace(/\s+Engine$/i, "").trim();
}

function buildRawKeyToEngineMap(descriptors: RawEngineDescriptor[] = []): Map<string, RawEngineDescriptor> {
  const map = new Map<string, RawEngineDescriptor>();
  if (!Array.isArray(descriptors) || descriptors.length < 2) return map;
  for (const descriptor of descriptors) {
    const channels = descriptor?.channels ?? {};
    for (const channelKey of Object.values(channels)) {
      if (typeof channelKey === "string" && channelKey) map.set(channelKey, descriptor);
    }
  }
  return map;
}

export function createRawMetricDescriptor(
  descriptors: RawEngineDescriptor[] = [],
): (key: string) => RawMetricEntry {
  const keyToEngine = buildRawKeyToEngineMap(descriptors);
  return function rawMetricDescriptorFromContext(key: string): RawMetricEntry {
    const entry = RAW_METRIC_DICTIONARY[key];
    if (!entry) return [titleCaseKey(key), "", 1];
    const prefix = rawMetricDescriptorLabelBase(keyToEngine.get(key));
    return prefix ? [`${prefix} ${entry[0]}`, entry[1], entry[2]] : entry;
  };
}

export const rawMetricDescriptor = createRawMetricDescriptor();

export function rawChannelKeysForData(points: Array<Record<string, unknown>> = []): string[] {
  const preferred = [
    "egt", ...Array.from({ length: 16 }, (_, index) => `egt${index + 1}`),
    "cht", ...Array.from({ length: 16 }, (_, index) => `cht${index + 1}`),
    "fuelFlow1", "fuelFlow2", "fuelFlow", "fuelUsed1", "fuelUsed2", "fuelUsed", "fuelPressure", "fuelPressure1", "fuelPressure2", "fuelLeft", "fuelRight",
    "coolantTemp", "coolantPressure",
    "rpm1", "rpm2", "rpm", "map1", "map2", "map", "percentPower",
    "powerRopHp", "percentPowerRop", "powerLopHp", "percentPowerLop",
    "upperDeckPressure", "tit", "tit1", "tit2",
    "inductionTemp", "compressorDischargeTemp", "manifoldTemp", "boostPressure", "throttlePosition", "buss", "amps1", "amps2", "amps", "alternatorAmps", "batteryAmps", "oat", "carbTemp",
    "oilTemp1", "oilTemp2", "oilTemp", "oilPressure1", "oilPressure2", "oilPressure",
    "dynonCanBoostPressure", "dynonCanEngineTime", "dynonCanBoostPressureError", "dynonCanOilPressureCompensation",
    "dynonCanTargetWastegatePosition", "dynonCanAmbientEngineTemp", "dynonCanAmbientEnginePressure",
    "fuelFlowLeft", "fuelFlowRight", "fuelUsedLeft", "fuelUsedRight",
    "rpmLeft", "rpmRight", "mapLeft", "mapRight", "ampsLeft", "ampsRight",
    "oilTempLeft", "oilTempRight", "oilPressureLeft", "oilPressureRight",
    "ias", "tas", "groundSpeed", "altitude", "densityAltitude", "pressureAltitude", "baroPressure", "verticalSpeed", "pitch", "roll",
  ];
  const reserved = new Set(["t", "timestamp", "displayTime", "phase", "phaseConfidence", "phaseEvidence", "phaseSource", "elapsedSeconds"]);
  const present = (key: string) => points.some((point) => Number.isFinite(point[key] as number));
  const extra = Object.keys(points[0] ?? {})
    .filter((key) => !reserved.has(key) && !preferred.includes(key) && present(key))
    .sort();
  return [...preferred, ...extra].filter(present);
}

export function formatRawNumber(value: unknown, digits = 1): string {
  if (!Number.isFinite(value)) return "n/a";
  return Number(value).toFixed(digits);
}

export function scoringRole(key: string): "Derived" | "Yes" | "No" {
  if (key === "egt" || key === "cht") return "Derived";
  if (/^(egt|cht)\d+$/.test(key) || ["buss", "carbTemp"].includes(key)) return "Yes";
  return "No";
}
